import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Stars } from './shared';
import { useIsMobile } from '../../hooks/useIsMobile';

// Badge with truncation + hover tooltip
function TruncBadge({ label, color, maxWidth = 180 }) {
  const [tip, setTip] = useState(false);
  const spanRef = useRef(null);
  const isTrunc = useRef(false);

  const handleEnter = () => {
    const el = spanRef.current;
    if (el) isTrunc.current = el.scrollWidth > el.clientWidth;
    setTip(true);
  };

  return (
    <span
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        maxWidth,
        cursor: 'default',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.5,
        color: color || 'rgba(255,255,255,0.7)',
        background: color ? color + '22' : 'rgba(255,255,255,0.1)',
        border: `1px solid ${color ? color + '45' : 'rgba(255,255,255,0.18)'}`,
        borderRadius: 6,
        padding: '3px 10px',
        flexShrink: 0,
      }}
      onMouseEnter={handleEnter}
      onMouseLeave={() => setTip(false)}
    >
      <span
        ref={spanRef}
        style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
      >
        {label}
      </span>
      {tip && isTrunc.current && (
        <div style={{
          position: 'absolute',
          bottom: 'calc(100% + 6px)',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#0f2d5c',
          color: '#e2e8f0',
          fontSize: 11,
          fontWeight: 500,
          lineHeight: 1.5,
          padding: '7px 12px',
          borderRadius: 8,
          border: '1px solid rgba(255,255,255,0.18)',
          whiteSpace: 'normal',
          maxWidth: 280,
          zIndex: 200,
          boxShadow: '0 6px 20px rgba(0,0,0,0.4)',
          pointerEvents: 'none',
          textAlign: 'center',
        }}>
          {label}
          <div style={{
            position: 'absolute',
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '5px solid transparent',
            borderRight: '5px solid transparent',
            borderTop: '5px solid rgba(255,255,255,0.18)',
          }} />
        </div>
      )}
    </span>
  );
}

const STAGE_COLOR = { Mainnet: '#059669', 'Mainnet Beta': '#0891b2', Testnet: '#d97706', Concept: '#dc2626' };

// Module-level cache: ticker → market data (avoids repeat CoinGecko calls when navigating)
const cgCache = {};

// ── Live market data fetchers ─────────────────────────────────────────────────
async function fetchBinance(sym) {
  const r = await fetch(`https://api.binance.com/api/v3/ticker/24hr?symbol=${sym}USDT`, { signal: AbortSignal.timeout(4000) });
  if (!r.ok) throw new Error('binance');
  const d = await r.json();
  if (d.code) throw new Error('binance');
  return { price: parseFloat(d.lastPrice), change24h: parseFloat(d.priceChangePercent), vol: parseFloat(d.quoteVolume), source: 'Binance' };
}

async function fetchMexc(sym) {
  const r = await fetch(`https://api.mexc.com/api/v3/ticker/24hr?symbol=${sym}USDT`, { signal: AbortSignal.timeout(4000) });
  if (!r.ok) throw new Error('mexc');
  const d = await r.json();
  if (d.code) throw new Error('mexc');
  return { price: parseFloat(d.lastPrice), change24h: parseFloat(d.priceChangePercent), vol: parseFloat(d.quoteVolume), source: 'MEXC' };
}

async function fetchOkx(sym) {
  const r = await fetch(`https://www.okx.com/api/v5/market/ticker?instId=${sym}-USDT`, { signal: AbortSignal.timeout(4000) });
  if (!r.ok) throw new Error('okx');
  const d = await r.json();
  const item = d?.data?.[0];
  if (!item) throw new Error('okx');
  return { price: parseFloat(item.last), change24h: parseFloat(item.changeUtc8 ?? item.chgUTC), vol: parseFloat(item.volCcy24h), source: 'OKX' };
}

async function fetchLivePrice(rawTicker) {
  const sym = rawTicker.replace(/^\$/, '').toUpperCase();
  if (!sym) return null;
  for (const fn of [fetchBinance, fetchMexc, fetchOkx]) {
    try { return await fn(sym); } catch { /* try next */ }
  }
  return null;
}

// Fetch JSON with 429 retry
async function cgFetch(url, attempt = 0) {
  const r = await fetch(url, { signal: AbortSignal.timeout(6000) });
  if (r.status === 429 && attempt < 2) {
    await new Promise(res => setTimeout(res, (attempt + 1) * 7000));
    return cgFetch(url, attempt + 1);
  }
  if (!r.ok) throw new Error('cg-' + r.status);
  return r.json();
}

// CoinGecko: price + market cap + FDV + circulating/total supply
async function fetchCoinGecko(rawTicker) {
  const sym = rawTicker.replace(/^\$/, '').toLowerCase();
  if (!sym) return null;

  const { coins } = await cgFetch(`https://api.coingecko.com/api/v3/search?query=${encodeURIComponent(sym)}`);
  const coin = coins.find(c => c.symbol.toLowerCase() === sym) || coins[0];
  if (!coin) throw new Error('cg-not-found');

  const [item] = await cgFetch(
    `https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&ids=${coin.id}&price_change_percentage=24h`
  );
  if (!item) throw new Error('cg-market-empty');

  return {
    price:       item.current_price,
    change24h:   item.price_change_percentage_24h,
    marketCap:   item.market_cap,
    fdv:         item.fully_diluted_valuation,
    vol:         item.total_volume,
    circSupply:  item.circulating_supply,
    totalSupply: item.total_supply,
    source:      'CoinGecko',
  };
}

function formatPrice(n) {
  if (!n || isNaN(n)) return null;
  if (n >= 1) return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  const s = n.toPrecision(4);
  return '$' + s;
}

function formatLargeNum(n) {
  if (!n || isNaN(n)) return null;
  if (n >= 1e9) return '$' + (n / 1e9).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + 'B';
  if (n >= 1e6) return '$' + (n / 1e6).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + 'M';
  if (n >= 1e3) return '$' + (n / 1e3).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + 'K';
  return '$' + n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function formatSupply(n) {
  if (!n || isNaN(n)) return null;
  if (n >= 1e9) return (n / 1e9).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + 'B';
  if (n >= 1e6) return (n / 1e6).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + 'M';
  if (n >= 1e3) return (n / 1e3).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + 'K';
  return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function HeaderCard({ overview, meta, verdict, tokenomics, doc, onBack, onRefresh }) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const stageColor = STAGE_COLOR[overview.stage] || '#6b8dad';
  const name   = doc.name || meta.name;
  const ticker = overview.ticker || meta.ticker || doc.ticker;

  // Only allow re-research when the latest report is older than 7 days
  const ageDays = doc.completedAt ? (Date.now() - new Date(doc.completedAt).getTime()) / 86400000 : null;
  const canRefresh = onRefresh && ageDays != null && ageDays >= 7;

  const [live, setLive]         = useState(null);  // CoinGecko full market data
  const [livePrice, setLivePrice] = useState(null); // fallback price-only from CEX
  const [liveLoading, setLiveLoading] = useState(false);
  const didFetch = useRef(false);

  useEffect(() => {
    if (!ticker || didFetch.current) return;
    didFetch.current = true;
    setLiveLoading(true);

    const key = ticker.replace(/^\$/, '').toLowerCase();
    // Return cached result immediately if available
    if (cgCache[key]) { setLive(cgCache[key]); setLiveLoading(false); return; }

    // Try CoinGecko first (full data); fall back to CEX for price only
    fetchCoinGecko(ticker)
      .then(r => { cgCache[key] = r; setLive(r); setLiveLoading(false); })
      .catch(() => {
        fetchLivePrice(ticker)
          .then(r => { setLivePrice(r); setLiveLoading(false); })
          .catch(() => setLiveLoading(false));
      });
  }, [ticker]);

  const isLive    = !!(live || livePrice);
  const price     = live ? formatPrice(live.price) : (livePrice ? formatPrice(livePrice.price) : null);
  const change    = live?.change24h ?? livePrice?.change24h;
  const changePos = (change ?? 0) >= 0;

  // Live values override report values when available
  const liveMcap   = live?.marketCap   ? formatLargeNum(live.marketCap)   : null;
  const liveFdv    = live?.fdv         ? formatLargeNum(live.fdv)         : null;
  const liveVol    = live?.vol         ? formatLargeNum(live.vol)         : null;
  const liveCirc   = live?.circSupply  ? formatSupply(live.circSupply)    : null;
  const liveTotal  = live?.totalSupply ? formatSupply(live.totalSupply)   : null;

  // Fallback to report/tokenomics
  const circSupply  = liveCirc  || tokenomics?.circulating;
  const totalSupply = liveTotal || tokenomics?.totalSupply;

  const priceLiveLabel = t('headerCard.priceLive');
  const statItems = [
    price
      ? { label: priceLiveLabel, value: price, isLive: true, change }
      : { label: t('headerCard.priceReport'), value: overview.price, isLive: false },
    { label: 'Market Cap', value: liveMcap  || overview.mcap,          isLive: !!liveMcap },
    { label: 'FDV',        value: liveFdv   || overview.fdv || doc.fdv, isLive: !!liveFdv  },
    { label: t('headerCard.volume24h'), value: liveVol || overview.vol, isLive: !!liveVol },
    { label: t('headerCard.circulating'), value: circSupply,           isLive: !!liveCirc  },
    { label: t('headerCard.totalSupply'), value: totalSupply,          isLive: !!liveTotal },
  ].filter(s => s.value);

  // All mobile overrides in one place — keeps responsive behavior auditable
  const mobile = isMobile ? {
    wrapper: { padding: '14px 16px' },
    main:    { flexDirection: 'column', alignItems: 'stretch', gap: 14 },
    stats:   { gridTemplateColumns: 'repeat(2, 1fr)', width: '100%' },
  } : {};

  return (
    <div style={{ ...styles.wrapper, ...mobile.wrapper }}>
      {/* Top bar: navigation left, verdict + actions right */}
      <div style={styles.topBar}>
        <button style={styles.backBtn} onClick={onBack}>← {t('headerCard.back')}</button>
        <div style={styles.topRight}>
          {verdict?.verdictStars > 0 && (
            <div style={styles.verdictBox} title={t('headerCard.verdictTooltip')}>
              <Stars n={verdict.verdictStars} size={17} />
              <span style={styles.verdictScore}>{verdict.verdictStars}/5</span>
            </div>
          )}
          {canRefresh && (
            <button style={styles.actionBtn} onClick={onRefresh} title={t('researchCard.reResearchTitle')}>
              🔄 {t('researchCard.reResearch')}
            </button>
          )}
        </div>
      </div>

      {/* Main row: identity | stats */}
      <div style={{ ...styles.main, ...mobile.main }}>
        <div style={styles.identity}>
          <div style={styles.nameRow}>
            <span style={styles.name} title={name}>{name}</span>
            {ticker && <span style={styles.ticker}>{ticker}</span>}
          </div>
          <div style={styles.tagRow}>
            {overview.stage && <TruncBadge label={overview.stage} color={stageColor} maxWidth={140} />}
            {overview.category && <TruncBadge label={overview.category} color="#22d3ee" maxWidth={140} />}
            {overview.chain    && <TruncBadge label={overview.chain}    color="#c4b5fd" maxWidth={140} />}
            {doc.category && doc.category !== overview.category && (
              <TruncBadge label={doc.category} color="#93c5fd" maxWidth={140} />
            )}
          </div>
        </div>

        <div style={{ ...styles.stats, ...mobile.stats }}>
          {liveLoading && !isLive && (
            <div style={styles.stat}>
              <span style={styles.statLabel}>{t('headerCard.liveData')}</span>
              <span style={{ ...styles.statValue, opacity: 0.5, fontSize: 12 }}>⟳ {t('headerCard.loading')}</span>
            </div>
          )}
          {statItems.map(s => (
            <div key={s.label} style={styles.stat}>
              <div style={styles.statLabelRow}>
                <span style={styles.statLabel}>{s.label}</span>
                {s.isLive && (
                  <span style={styles.liveBadge}>● LIVE</span>
                )}
              </div>
              <div style={styles.statValueRow}>
                <span style={styles.statValue}>{s.value}</span>
                {s.label === priceLiveLabel && change != null && !isNaN(change) && (
                  <span style={{ ...styles.changeChip, color: changePos ? '#4ade80' : '#f87171' }}>
                    {changePos ? '▲' : '▼'} {Math.abs(change).toFixed(2)}%
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const styles = {
  wrapper: {
    background: 'linear-gradient(135deg, #1a3f7a 0%, #0f2d5c 100%)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 16,
    padding: '16px 22px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    boxShadow: '0 4px 20px rgba(15,45,92,0.25)',
    maxWidth: '100%',
    overflow: 'hidden',
  },

  // ── Top bar ──
  topBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' },
  topRight: { display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' },
  backBtn: {
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.2)',
    color: 'rgba(255,255,255,0.85)',
    borderRadius: 8,
    padding: '6px 14px',
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    transition: 'background 0.2s',
  },
  verdictBox: {
    display: 'flex', alignItems: 'center', gap: 7,
    background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.25)',
    borderRadius: 8, padding: '4px 12px',
  },
  verdictScore: { fontSize: 15, fontWeight: 800, color: '#fbbf24' },
  actionBtn: {
    background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)',
    color: '#ffffff', borderRadius: 8, padding: '7px 14px',
    cursor: 'pointer', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
    transition: 'background 0.2s',
  },

  // ── Main row ──
  main: { display: 'flex', alignItems: 'flex-start', gap: 24 },
  identity: { display: 'flex', flexDirection: 'column', gap: 10, flex: '1 1 260px', minWidth: 0 },
  nameRow: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'nowrap', minWidth: 0 },
  name: {
    fontSize: 24, fontWeight: 800, color: '#ffffff', letterSpacing: -0.5, lineHeight: 1.2,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    minWidth: 0, flexShrink: 1,
  },
  ticker: {
    fontSize: 13, fontWeight: 700, color: '#7dd3fc',
    background: 'rgba(125,211,252,0.15)', border: '1px solid rgba(125,211,252,0.3)',
    borderRadius: 6, padding: '3px 10px', whiteSpace: 'nowrap', flexShrink: 0,
  },
  tagRow: { display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', rowGap: 6, width: '100%' },

  // Stats: clean tiles on a grid — no border-collapse juggling
  stats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(130px, 1fr))',
    gap: 6,
    width: 'min(480px, 100%)',
    flexShrink: 0,
  },
  stat: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    padding: '8px 11px',
    background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 10,
    minWidth: 0,
  },
  statLabelRow: { display: 'flex', alignItems: 'center', gap: 5 },
  statLabel: {
    fontSize: 9, color: 'rgba(255,255,255,0.55)', fontWeight: 600, letterSpacing: 0.7,
    textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  liveBadge: {
    fontSize: 7, fontWeight: 800, color: '#4ade80', letterSpacing: 0.4,
    background: 'rgba(74,222,128,0.15)', border: '1px solid rgba(74,222,128,0.3)',
    borderRadius: 3, padding: '1px 3px', flexShrink: 0,
  },
  statValueRow: { display: 'flex', alignItems: 'baseline', gap: 5, minWidth: 0, flexWrap: 'wrap' },
  statValue: { fontSize: 14, fontWeight: 700, color: '#ffffff', wordBreak: 'break-word', overflowWrap: 'break-word' },
  changeChip: { fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' },
};
